import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { cn } from '@/utils';
import { IoCloseSharp } from 'react-icons/io5';

interface BannerItem {
  image?: string;
  type?: string;
}

interface WelcomeBannerProps {
  banners?: BannerItem[];
  className?: string;
  isLoading?: boolean;
  setShowWelcomeBanner: (show: boolean) => void;
}

export const WelcomeBanner: React.FC<WelcomeBannerProps> = ({
  banners,
  className,
  isLoading,
  setShowWelcomeBanner,
}) => {
  const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({});

  const allLoaded =
    Array.isArray(banners) && banners.length > 0 && banners.every((_, idx) => imagesLoaded[idx]);

  // Show skeleton while data is still fetching OR while images haven't loaded yet
  const showSkeleton = isLoading || !allLoaded;
  const hasMultipleSlides = (banners?.length ?? 0) > 1;

  return (
    <div
      onClick={(e) => setShowWelcomeBanner(false)}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-3"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative w-full max-w-[440px] border border-primary rounded-[0.625rem] animate-fade-in h-[85dvh] overflow-y-auto',
          className
        )}
      >
        <button
          type="button"
          aria-label="Close welcome banner"
          className="absolute right-2 top-2 z-20 flex size-8 items-center justify-center rounded-full bg-black text-xl leading-none text-white"
          onClick={() => setShowWelcomeBanner(false)}
        >
          <IoCloseSharp />
        </button>

        {hasMultipleSlides && (
          <>
            <button className="banner-prev rotate-90 absolute left-3 top-1/2 z-10 -translate-y-1/2">
              <i className="icon-arrow-down text-[1.5rem] opacity-70" />
            </button>

            <button className="banner-next -rotate-90 absolute right-3 top-1/2 z-10 -translate-y-1/2 text-white">
              <i className="icon-arrow-down text-[1.5rem] opacity-70" />
            </button>
          </>
        )}

        {/* ── Slider ── always rendered so images can preload; hidden until ready */}
        {Array.isArray(banners) && banners.length > 0 && (
          <Swiper
            modules={[Autoplay, Navigation]}
            navigation={
              hasMultipleSlides
                ? {
                    prevEl: '.banner-prev',
                    nextEl: '.banner-next',
                  }
                : false
            }
            autoplay={
              hasMultipleSlides
                ? {
                    delay: 5000,
                    disableOnInteraction: false,
                  }
                : false
            }
            loop={hasMultipleSlides}
            className="w-full h-full"
          >
            {banners.map((banner, idx) => (
              <SwiperSlide key={idx}>
                <img
                  src={banner.image}
                  alt={banner.type}
                  className="w-full h-full object-fill rounded-[0.625rem] object-center"
                  onLoad={() => setImagesLoaded((prev) => ({ ...prev, [idx]: true }))}
                  onError={() => setImagesLoaded((prev) => ({ ...prev, [idx]: true }))}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </div>
  );
};
